import React, { useEffect, useState } from 'react'
import styles from './matchCard.module.css'
import { NavLink } from 'react-router-dom'
import { supabase } from '../../utils/supabaseClient'
import { Match } from '../../types/matchTypes'

interface City {
	id: number
	city: string
}
interface Country {
	id: number
	country: string
}
interface City2Country {
	id: number
	city: City
	country: Country
}
interface Club {
	id: number
	short_name: string
	logo: string
	city2country: City2Country
}
interface Stadium {
	id: number
	name: string
}
interface Tournament {
	id: number
	name: string
	type: string
	short_name?: string
}
interface MatchTeam {
	id: number
	name: string
	fk_club: number
	club: Club
}
interface MatchEvent {
	id: number
	fk_period2match: number
	fk_event_type: number
	fk_persona: number
	opt_add_time: number | null
	description: string | null
	time: number
	fk_parent: number | null
	match_event_types: {
		event_type: string
	}
	persona: {
		id: number
		persona2team: {
			fk_team: number
		}[]
	}
}
interface MatchCardProps {
	match: Match
}

const MatchCard: React.FC<MatchCardProps> = ({ match }) => {
	const [homeGoals, setHomeGoals] = useState(0)
	const [awayGoals, setAwayGoals] = useState(0)

	const fetchEvents = async () => {
		try {
			console.log(`Fetching events for match ID: ${match.id}`);
			const { data: periodsData, error: periodsError } = await supabase
				.from('period2match')
				.select('id')
				.eq('fk_match', match.id);

			if (periodsError) {
				throw periodsError;
			}

			const periodIds = periodsData.map((period: any) => period.id);

			const { data: eventsData, error: eventsError } = await supabase
				.from('match_events')
				.select(`
        id,
        fk_period2match,
        fk_event_type,
        fk_persona,
        opt_add_time,
        description,
        time,
        fk_parent,
        match_event_types (
          event_type
        ),
        persona:personas (
          id,
          persona2team (
            fk_team
          )
        )
      `)
				.in('fk_period2match', periodIds);

			if (eventsError) {
				throw eventsError;
			}

			console.log('Events data:', eventsData);

			// Calculate scores
			let homeGoals = 0;
			let awayGoals = 0;

			eventsData.forEach((event: any) => {
				const eventType = event.match_event_types.event_type;
				const personaTeamId = event.persona.persona2team[0]?.fk_team;

				if (eventType === 'goal' || eventType === 'penalty kick') {
					if (personaTeamId === match.fk_team_host) {
						homeGoals++;
					} else if (personaTeamId === match.fk_team_guest) {
						awayGoals++;
					}
				} else if (eventType === 'owngoal') {
					if (personaTeamId === match.fk_team_host) {
						awayGoals++;
					} else if (personaTeamId === match.fk_team_guest) {
						homeGoals++;
					}
				}
			});

			console.log('Home Goals:', homeGoals, 'Away Goals:', awayGoals);
			setHomeGoals(homeGoals);
			setAwayGoals(awayGoals);
		} catch (error) {
			console.error('Error fetching match events:', error);
		}
	};

	useEffect(() => {
		fetchEvents()
	}, [match.id])

	const formatTime = (dateString: string) => {
		const date = new Date(dateString)
		return date.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	const formatTimezone = (dateString: string) => {
		const date = new Date(dateString)
		return date
			.toLocaleTimeString('en-US', {
				timeZoneName: 'short',
			})
			.split(' ')
			.pop()
	}

	const formattedTime = formatTime(match.actual_start_time)
	const formattedTimezone = formatTimezone(match.actual_start_time)

	const matchName = match.match_name ||
		(match.tournament_short_name
			? match.tournament_short_name +
			(match.stage_name ? ', ' + match.stage_name : '') +
			(match.round_name ? ', ' + match.round_name : '')
			: 'Unnamed Match');

	const scoreClass =
		match.status === 'Running'
			? styles.match_card__score_live
			: match.status === 'Played' || match.status === 'Officialised'
				? styles.match_card__score_final
				: styles.match_card__score

	return (
		<li className={styles.calendar__item} key={match.id}>
			<div className={styles.calendar__item_top}>
				<div className={styles.calendar__item_top_button}>
					<button className={styles.calendar__item__button}>
						<img src='http://pitchinvasion.ru/files/icons/ticket.svg' />
					</button>
				</div>
				<div>
					<p>{formattedTime}</p>
					<p>{matchName}</p>
				</div>
				<div className={styles.calendar__item_top_button}>
					<NavLink to={`/matches/${match.id}`}>
						<button className={styles.calendar__item__button}>
							<img src='http://pitchinvasion.ru/files/icons/arrow.svg' />
						</button>
					</NavLink>
				</div>
			</div>
			<div className={styles.calendar__item_main}>
				<div className={styles.calendar__item__time_zone}>
					<p>{formattedTime}</p>
					<p>({formattedTimezone})</p>
				</div>
				<div className={styles.calendar__item__tournament_stadium}>
					<p>{matchName}</p>
					{match.stadium_name && <p>{match.stadium_name}</p>}
				</div>
				<div className={styles.match_card__scoreboard_S}>
					{match.host_team_name && match.host_club_logo && (
						<div className={styles.match_card__home_team}>
							<div className={styles.match_card__home_team_name}>
								<p>{match.host_team_name}</p>
								<p>{match.host_city_name}</p>
							</div>
							<div className={styles.match_card__team_logo__container}>
								<img
									className={styles.match_card__team_logo}
									src={match.host_club_logo}
									alt={match.host_team_name}
								/>
							</div>
						</div>
					)}
					<div className={styles.match_card__score_container}>
						<div className={`${styles.match_card__score} ${scoreClass}`}>
							<span>{homeGoals}</span>
							<span>–</span>
							<span>{awayGoals}</span>
						</div>
						{match.status === 'Running' && (
							<div className={styles.match__score__live_tag}>
								<span>Live</span>
							</div>
						)}
					</div>
					{match.guest_team_name && match.guest_club_logo && (
						<div className={styles.match_card__away_team}>
							<div className={styles.match_card__team_logo__container}>
								<img
									className={styles.match_card__team_logo}
									src={match.guest_club_logo}
									alt={match.guest_team_name}
								/>
							</div>
							<div className={styles.match_card__team_name}>
								<p>{match.guest_team_name}</p>
								<p>{match.guest_city_name}</p>
							</div>
						</div>
					)}
				</div>
				<div className={styles.calendar__item__buttons}>
					<button
						className={`${styles.calendar__item__button} ${styles.calendar__item__button__tickets}`}
					>
						<img src='http://pitchinvasion.ru/files/icons/ticket.svg' />
						<span className={styles.calendar__item__button__text}>
							Купить билет
						</span>
					</button>
					<NavLink to={`/matches/${match.id}`}>
						<button
							className={`${styles.calendar__item__button} ${styles.calendar__item__button__no_border}`}
						>
							<span className={styles.calendar__item__button__text}>
								Матч-центр
							</span>
							<span className={styles.calendar__item__button__icon_only}>
								<img src='http://pitchinvasion.ru/files/icons/arrow.svg' />
							</span>
						</button>
					</NavLink>
				</div>
			</div>
			{match.stadium_name && (
				<span className={styles.calendar__item_bottom}>
					{match.stadium_name}
				</span>
			)}
		</li>
	)
}

export default MatchCard
